import { Typography } from "@mui/material";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import { useTranslation } from "react-i18next";
import SummaryStatTiles from "@/components/summaryCard/summaryStatTiles";
import SummaryIssueList, {
  SummaryIssue,
} from "@/components/summaryCard/summaryIssueList";
import cardStyles from "@/components/summaryCard/summaryCard.module.css";

export interface MissingTranslationsImportResultData {
  accepted: number;
  created_translations: number;
  created_relations: number;
  skipped: { row: number; reason: string }[];
  errors: { row: number; field: string; detail: string }[];
}

interface RowMessage {
  row: number;
  message: string;
}

interface Props {
  fileName: string;
  result: MissingTranslationsImportResultData;
}

const groupMessagesByRow = (rowMessages: RowMessage[]) => {
  const messagesByRow = new Map<number, Set<string>>();
  rowMessages.forEach(({ row, message }) => {
    messagesByRow.set(row, (messagesByRow.get(row) ?? new Set()).add(message));
  });
  return [...messagesByRow.entries()].sort(([rowA], [rowB]) => rowA - rowB);
};

export default function MissingTranslationsImportResult({
  fileName,
  result,
}: Props) {
  const { t } = useTranslation();

  const toIssuesPerRow = (rowMessages: RowMessage[]): SummaryIssue[] =>
    groupMessagesByRow(rowMessages).map(([row, messages]) => ({
      key: String(row),
      title: t("row_number", { row }),
      details: [...messages],
    }));

  const errorRows = toIssuesPerRow(
    result.errors.map(({ row, field, detail }) => ({
      row,
      message: `${field}: ${detail}`,
    }))
  );
  const skippedRows = toIssuesPerRow(
    result.skipped.map(({ row, reason }) => ({ row, message: reason }))
  );

  return (
    <section className={cardStyles.card}>
      <div className={cardStyles.header}>
        <span className={cardStyles.iconContainer} aria-hidden="true">
          <TranslateOutlinedIcon />
        </span>
        <div className={cardStyles.headerCopy}>
          <Typography className={cardStyles.headerLabel}>
            {t("file_name")}
          </Typography>
          <Typography className={cardStyles.headerValue}>{fileName}</Typography>
        </div>
      </div>

      <SummaryStatTiles
        stats={[
          {
            label: t("missing_translations_import_accepted_rows"),
            value: result.accepted,
          },
          {
            label: t("missing_translations_import_created_translations"),
            value: result.created_translations,
          },
          {
            label: t("missing_translations_import_created_relations"),
            value: result.created_relations,
          },
          {
            label: t("missing_translations_import_skipped_rows"),
            value: skippedRows.length,
          },
          {
            label: t("missing_translations_import_error_rows"),
            value: errorRows.length,
            isFailureCount: true,
          },
        ]}
      />

      <SummaryIssueList
        heading={t("missing_translations_import_error_rows")}
        issues={errorRows}
      />
      <SummaryIssueList
        heading={t("missing_translations_import_skipped_rows")}
        issues={skippedRows}
        severity="warning"
      />
    </section>
  );
}
